import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Save, X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FieldError } from '../ui/FieldError';
import { venuesAPI } from '../../lib/api';
import { isValidEmail, inputClass } from '../../lib/validation';
import type { Student, StudentFormData, Venue } from '../../types';

interface StudentFormProps {
  initialData?: Student;
  onSubmit: (data: StudentFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function StudentForm({ initialData, onSubmit, onCancel, isLoading }: StudentFormProps) {
  const { data: venues } = useQuery({
    queryKey: ['venues'],
    queryFn: () => venuesAPI.getAll(),
  });

  // Campos numéricos se manejan como string en el input y se convierten al enviar.
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    birthDate: initialData?.birthDate ? initialData.birthDate.split('T')[0] : '',
    notes: initialData?.notes || '',
    trainingLevel: (initialData?.trainingLevel ?? '') as '' | NonNullable<StudentFormData['trainingLevel']>,
    attentionType: (initialData?.attentionType ?? '') as '' | NonNullable<StudentFormData['attentionType']>,
    otherSports: initialData?.otherSports || '',
    healthNotes: initialData?.healthNotes || '',
    venueId: initialData?.venueId || '',
    sex: (initialData?.sex ?? '') as '' | NonNullable<StudentFormData['sex']>,
    heightCm: initialData?.heightCm != null ? String(initialData.heightCm) : '',
    weightKg: initialData?.weightKg != null ? String(initialData.weightKg) : '',
    goal: initialData?.goal || '',
    isActive: initialData?.isActive ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = 'El nombre es obligatorio';
    if (!formData.lastName.trim()) e.lastName = 'El apellido es obligatorio';
    if (!formData.email.trim()) e.email = 'El email es obligatorio';
    else if (!isValidEmail(formData.email)) e.email = 'Ingresá un email válido (ej: juan@email.com)';
    if (formData.heightCm && Number(formData.heightCm) <= 0) e.heightCm = 'La altura debe ser mayor a 0';
    if (formData.weightKg && Number(formData.weightKg) <= 0) e.weightKg = 'El peso debe ser mayor a 0';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Convertir vacíos a undefined (los enums/números del API no aceptan '').
    onSubmit({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      birthDate: formData.birthDate || undefined,
      notes: formData.notes || undefined,
      goal: formData.goal || undefined,
      trainingLevel: formData.trainingLevel || undefined,
      attentionType: formData.attentionType || undefined,
      otherSports: formData.otherSports || undefined,
      healthNotes: formData.healthNotes || undefined,
      venueId: formData.venueId || undefined,
      sex: formData.sex || undefined,
      heightCm: formData.heightCm ? Number(formData.heightCm) : undefined,
      weightKg: formData.weightKg ? Number(formData.weightKg) : undefined,
      isActive: formData.isActive,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    // Limpiar el error del campo al editarlo.
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="label">Nombre *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            aria-invalid={!!errors.firstName}
            className={inputClass(!!errors.firstName)}
            placeholder="Juan"
          />
          <FieldError message={errors.firstName} />
        </div>
        <div>
          <label htmlFor="lastName" className="label">Apellido *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            aria-invalid={!!errors.lastName}
            className={inputClass(!!errors.lastName)}
            placeholder="Pérez"
          />
          <FieldError message={errors.lastName} />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="label">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          aria-invalid={!!errors.email}
          className={inputClass(!!errors.email)}
          placeholder="juan@email.com"
        />
        <FieldError message={errors.email} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="label">Teléfono</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input"
            placeholder="+54 11 1234-5678"
          />
        </div>
        <div>
          <label htmlFor="birthDate" className="label">Fecha de Nacimiento</label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleChange}
            className="input"
          />
        </div>
      </div>

      <div>
        <label htmlFor="trainingLevel" className="label">Nivel de entrenamiento</label>
        <select
          id="trainingLevel"
          name="trainingLevel"
          value={formData.trainingLevel ?? ''}
          onChange={handleChange}
          className="input"
        >
          <option value="">Sin especificar</option>
          <option value="beginner">Principiante</option>
          <option value="intermediate">Intermedio</option>
          <option value="advanced">Avanzado</option>
        </select>
        <p className="text-xs text-dark-500 mt-1">
          Se usa para evaluar si la carga de cada día es adecuada para el alumno.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="attentionType" className="label">Modalidad de atención</label>
          <select
            id="attentionType"
            name="attentionType"
            value={formData.attentionType ?? ''}
            onChange={handleChange}
            className="input"
          >
            <option value="">Sin especificar</option>
            <option value="in_person">Presencial</option>
            <option value="remote">A distancia</option>
          </select>
        </div>
        <div>
          <label htmlFor="venueId" className="label">Sede de entrenamiento</label>
          <select
            id="venueId"
            name="venueId"
            value={formData.venueId}
            onChange={handleChange}
            className="input"
          >
            <option value="">Sin asignar</option>
            {venues?.map((v: Venue) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          {(!venues || venues.length === 0) && (
            <p className="text-xs text-dark-500 mt-1">
              Cargá sedes desde tu perfil para poder asignarlas.
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="otherSports" className="label">Otros deportes que practica</label>
        <input
          type="text"
          id="otherSports"
          name="otherSports"
          value={formData.otherSports}
          onChange={handleChange}
          className="input"
          placeholder="Fútbol, escalada, running..."
        />
        <p className="text-xs text-dark-500 mt-1">
          La fuerza suele ser complementaria: ayuda a dosificar la carga.
        </p>
      </div>

      <div>
        <label htmlFor="healthNotes" className="label">Lesiones / condiciones de salud</label>
        <textarea
          id="healthNotes"
          name="healthNotes"
          value={formData.healthNotes}
          onChange={handleChange}
          rows={2}
          className="input resize-none"
          placeholder="Lesiones actuales o pasadas, condiciones médicas, medicación..."
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="heightCm" className="label">Altura (cm)</label>
          <input
            type="number"
            inputMode="numeric"
            id="heightCm"
            name="heightCm"
            value={formData.heightCm}
            onChange={handleChange}
            aria-invalid={!!errors.heightCm}
            className={inputClass(!!errors.heightCm)}
            placeholder="175"
            min="0"
          />
          <FieldError message={errors.heightCm} />
        </div>
        <div>
          <label htmlFor="weightKg" className="label">Peso (kg)</label>
          <input
            type="number"
            inputMode="decimal"
            id="weightKg"
            name="weightKg"
            value={formData.weightKg}
            onChange={handleChange}
            aria-invalid={!!errors.weightKg}
            className={inputClass(!!errors.weightKg)}
            placeholder="72.5"
            min="0"
            step="0.1"
          />
          <FieldError message={errors.weightKg} />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label htmlFor="sex" className="label">Sexo</label>
          <select
            id="sex"
            name="sex"
            value={formData.sex}
            onChange={handleChange}
            className="input"
          >
            <option value="">Sin especificar</option>
            <option value="male">Masculino</option>
            <option value="female">Femenino</option>
            <option value="other">Otro</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="goal" className="label">Objetivo</label>
        <input
          type="text"
          id="goal"
          name="goal"
          value={formData.goal}
          onChange={handleChange}
          className="input"
          placeholder="Hipertrofia, fuerza, recomposición..."
        />
      </div>

      <div>
        <label htmlFor="notes" className="label">Notas</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="input resize-none"
          placeholder="Objetivos, lesiones, observaciones..."
        />
      </div>

      {initialData && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
          />
          <span className="text-dark-300 text-sm">Alumno activo</span>
        </label>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar Cambios' : 'Crear Alumno'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

